import React, { Suspense, useCallback, useRef } from 'react';
import type { Dayjs } from 'dayjs';
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    DownloadIcon,
    XIcon,
} from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Button } from '@rocco/components/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
} from '@rocco/components/dialog';
import { Spinner } from '@rocco/components/spinner';
import { isPdfFile } from '~/modules/file';
import { formatDateFull } from '../../utils';
import { ImageViewer } from './ImageViewer';

/**
 * Lazy-loaded PDF viewer. Keeps `react-pdf` (which depends on browser-only
 * APIs like `DOMMatrix`) out of the SSR module graph.
 */
const LazyPDFViewer = React.lazy(() =>
    import('./PDFViewer').then((mod) => ({ default: mod.PDFViewer })),
);

export interface LightboxItem {
    id: number;
    url: string;
    filename: string;
    date?: Dayjs;
}

/**
 * A full-screen lightbox for chat message attachments.
 *
 * Supports two content types:
 * - **Images**: Zoom and pan.
 * - **PDFs**: Page thumbnails sidebar and page viewer.
 */
export function ChatLightbox({
    open,
    index,
    items,
    onOpenChange,
    onIndexChange,
}: {
    open: boolean;
    index: number;
    items: LightboxItem[];
    onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
    onIndexChange: React.Dispatch<React.SetStateAction<number>>;
}) {
    const hasMultiple = items.length > 1;
    const currentItem = items[index];
    const isPdf = isPdfFile(currentItem?.filename);

    const imgContainerRef = useRef<HTMLDivElement>(null);

    const goToPrevious = useCallback(() => {
        onIndexChange((prev) => (prev > 0 ? prev - 1 : items.length - 1));
    }, [items.length, onIndexChange]);

    const goToNext = useCallback(() => {
        onIndexChange((prev) => (prev < items.length - 1 ? prev + 1 : 0));
    }, [items.length, onIndexChange]);

    function handleKeyDown(e: React.KeyboardEvent) {
        switch (e.key) {
            case 'ArrowLeft':
                if (hasMultiple) {
                    e.preventDefault();
                    goToPrevious();
                }
                break;
            case 'ArrowRight':
                if (hasMultiple) {
                    e.preventDefault();
                    goToNext();
                }
                break;
        }
    }

    if (items.length === 0 || !currentItem) {
        return null;
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className={cn(
                    'flex flex-col gap-0',
                    isPdf ? 'bg-[#3D3938]' : 'bg-black/90',
                )}
                variant="fullScreen"
                spacing="none"
                closeButton={false}
                onKeyDown={handleKeyDown}
            >
                {/* Suppress a11y console warnings. */}
                <DialogTitle className="hidden" aria-hidden />
                <DialogDescription className="hidden" aria-hidden />

                {/* Top bar: counter, filename, download, close. */}
                <div className="z-1 flex items-center gap-3 border-b border-white/20 px-4 py-3 text-4 leading-tight text-white/80">
                    {hasMultiple && (
                        <span className="shrink-0 tabular-nums">
                            {index + 1} / {items.length}
                        </span>
                    )}
                    <span className="min-w-0 flex-1 truncate whitespace-pre">
                        {currentItem.filename}
                        {currentItem.date &&
                            `  ·  ${formatDateFull(currentItem.date)}`}
                    </span>
                    <div className="flex shrink-0 items-center gap-2">
                        <Button
                            asChild
                            className="size-12 rounded-full p-0 text-white/70 hover:bg-white/10 hover:text-white"
                            color="transparent"
                            aria-label="Download file"
                        >
                            <a
                                href={currentItem.url}
                                target="_blank"
                                rel="noopener"
                                download
                            >
                                <DownloadIcon className="size-6" />
                            </a>
                        </Button>
                        <Button
                            className="size-12 rounded-full p-0 text-white/70 hover:bg-white/10 hover:text-white"
                            color="transparent"
                            onClick={() => onOpenChange(false)}
                            aria-label="Close"
                        >
                            <XIcon className="size-6" />
                        </Button>
                    </div>
                </div>

                {/* File content viewer area. */}
                <div
                    ref={imgContainerRef}
                    className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden px-20 py-8"
                    onClick={(e) => {
                        // Close when click on dark area.
                        if (e.target === e.currentTarget) {
                            onOpenChange(false);
                        }
                    }}
                >
                    {isPdf ? (
                        <Suspense
                            fallback={
                                <div className="grid size-full place-items-center">
                                    <Spinner className="size-12 text-white/60" />
                                </div>
                            }
                        >
                            <LazyPDFViewer
                                key={currentItem.id}
                                className="size-full"
                                url={currentItem.url}
                            />
                        </Suspense>
                    ) : (
                        <ImageViewer
                            key={currentItem.id}
                            url={currentItem.url}
                            alt={currentItem.filename}
                            containerRef={imgContainerRef}
                        />
                    )}
                    {/* Next and previous buttons. */}
                    {hasMultiple && (
                        <>
                            <Button
                                className="absolute left-2 top-1/2 z-1 size-12 -translate-y-1/2 rounded-full bg-black p-0 text-white/80 hover:bg-black hover:text-white"
                                color="dark"
                                aria-label="Previous image"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    goToPrevious();
                                }}
                            >
                                <ChevronLeftIcon className="size-6" />
                            </Button>
                            <Button
                                className="absolute right-2 top-1/2 z-1 size-12 -translate-y-1/2 rounded-full bg-black p-0 text-white/80 hover:bg-black hover:text-white"
                                color="dark"
                                aria-label="Next image"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    goToNext();
                                }}
                            >
                                <ChevronRightIcon className="size-6" />
                            </Button>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
